import { ReactNode, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Home, FileText, Settings, Building2, GraduationCap, User, Bell, BookOpen } from 'lucide-react';
import { signOut, authenticatedFetch } from '../lib/auth';
import { socket } from '../lib/socket';
import type { User as UserType } from '../types';
import VersionNotificationsPanel from './VersionNotificationsPanel';

interface LayoutProps {
  children: ReactNode;
  user: UserType;
}

export default function Layout({ children, user }: LayoutProps) {
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [versionNotificationsCount, setVersionNotificationsCount] = useState(0);
  const [showVersionNotifications, setShowVersionNotifications] = useState(false);
  const [hasApprovedLmsIntegration, setHasApprovedLmsIntegration] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      // Use window.location to force full page reload and update App state
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Fetch pending users count on mount (only for admin/systemadmin)
  useEffect(() => {
    console.log('[Layout] useEffect triggered, user role:', user.role);

    if (user.role === 'admin' || user.role === 'systemadmin') {
      console.log('[Layout] Fetching pending users count for admin/systemadmin');

      const fetchPendingCount = async () => {
        try {
          console.log('[Layout] Calling /api/admin/pending-users-count');
          const response = await authenticatedFetch('/api/admin/pending-users-count', {
            method: 'POST',
            body: JSON.stringify({ admin_id: user.id }),
          });

          const result = await response.json();
          console.log('[Layout] Pending count result:', result);

          if (result.success) {
            setPendingUsersCount(result.count);
            console.log('[Layout] Set pending count to:', result.count);
          }
        } catch (error) {
          console.error('Failed to fetch pending users count:', error);
        }
      };

      fetchPendingCount();

      // Listen to Socket.io events for real-time updates
      console.log('[Layout] Setting up Socket.io listeners');

      const handleUserPendingApproval = () => {
        console.log('[Layout] Socket.io event: user_pending_approval');
        fetchPendingCount();
      };

      const handleUserApproved = () => {
        console.log('[Layout] Socket.io event: user_approved');
        fetchPendingCount();
      };

      socket.on('user_pending_approval', handleUserPendingApproval);
      socket.on('user_approved', handleUserApproved);
      console.log('[Layout] Socket.io listeners registered');

      return () => {
        console.log('[Layout] Cleaning up Socket.io listeners');
        socket.off('user_pending_approval', handleUserPendingApproval);
        socket.off('user_approved', handleUserApproved);
      };
    } else {
      console.log('[Layout] User is not admin/systemadmin, skipping pending count fetch');
    }
  }, [user.id, user.role]);

  // Check if user has any approved LMS integrations (for "LMS-kurs" menu visibility)
  useEffect(() => {
    const checkLmsApproved = async () => {
      try {
        const r = await authenticatedFetch('/api/lms-courses/has-approved');
        if (r.ok) {
          const data = await r.json();
          setHasApprovedLmsIntegration(!!data.has_approved);
        }
      } catch (err) {
        // silently ignore - menu just won't appear
      }
    };
    checkLmsApproved();
  }, [user.id, user.role]);

  // Fetch version notifications count for member users (who have LMS integrations)
  useEffect(() => {
    if (user.role === 'member') {
      const fetchVersionNotificationsCount = async () => {
        try {
          const response = await authenticatedFetch('/api/version-notifications/count');
          const result = await response.json();
          if (result.success) {
            setVersionNotificationsCount(result.count);
          }
        } catch (error) {
          console.error('Failed to fetch version notifications count:', error);
        }
      };

      fetchVersionNotificationsCount();

      // Refresh count every 5 minutes
      const interval = setInterval(fetchVersionNotificationsCount, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user.id, user.role]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-kf-primary text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 sm:h-16 gap-3 sm:gap-0">
            <div className="flex items-center">
              <h1 className="text-xl sm:text-2xl font-bold">Kompetanseattest</h1>
              <span className="ml-2 sm:ml-4 text-xs sm:text-sm opacity-80">Kvalitetssikring</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              {/* Version notifications bell - only for member users with LMS integrations */}
              {user.role === 'member' && (
                <button
                  onClick={() => setShowVersionNotifications(true)}
                  className="relative p-2 hover:bg-kf-secondary rounded-md transition-colors"
                  title="Versjonsvarsler"
                >
                  <Bell size={18} />
                  {versionNotificationsCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                      {versionNotificationsCount > 9 ? '9+' : versionNotificationsCount}
                    </span>
                  )}
                </button>
              )}
              <Link
                to="/profile"
                className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm hover:underline"
                title="Min profil"
              >
                <User size={16} className="flex-shrink-0" />
                <span className="truncate max-w-[150px] sm:max-w-none">{user.email}</span>
              </Link>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 rounded-md hover:bg-kf-secondary transition-colors text-sm"
              >
                <LogOut size={18} className="flex-shrink-0" />
                <span>Logg ut</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 sm:gap-4 lg:space-x-8 py-2 min-h-14">
            <NavLink to="/" icon={<Home size={18} />}>
              Hjem
            </NavLink>
            <NavLink to="/study-plans" icon={<FileText size={18} />}>
              Studieplaner og litteratur
            </NavLink>
            {/* LMS-kurs - only visible for users with approved LMS integrations */}
            {hasApprovedLmsIntegration && (
              <NavLink to="/lms-courses" icon={<BookOpen size={18} />}>
                LMS-kurs
              </NavLink>
            )}
            {/* Kurs og prøver - consolidated menu for member, admin, systemadmin */}
            {(user.role === 'member' || user.role === 'admin' || user.role === 'systemadmin') && (
              <NavLink to="/courses-and-exams" icon={<GraduationCap size={18} />}>
                Kurs og prøver
              </NavLink>
            )}
            {/* Sensor Dashboard - only for sensor role */}
            {user.role === 'sensor' && (
              <NavLink to="/sensor/dashboard" icon={<GraduationCap size={18} />}>
                Sensor
              </NavLink>
            )}
            {/* Members and Users management - for admin, systemadmin, and member roles */}
            {(user.role === 'admin' || user.role === 'systemadmin' || user.role === 'member') && (
              <NavLink
                to="/members"
                icon={<Building2 size={18} />}
                badge={(user.role === 'admin' || user.role === 'systemadmin') && pendingUsersCount > 0 ? pendingUsersCount : undefined}
              >
                {user.role === 'member' ? 'Min organisasjon' : 'Organisasjoner'}
              </NavLink>
            )}
            {/* Study association management - only for systemadmin */}
            {user.role === 'systemadmin' && (
              <NavLink to="/study-associations" icon={<Building2 size={18} />}>
                Studieforbund
              </NavLink>
            )}
            {/* System Settings - only for systemadmin */}
            {user.role === 'systemadmin' && (
              <NavLink to="/settings" icon={<Settings size={18} />}>
                Systeminnstillinger
              </NavLink>
            )}
            {/* Admin Settings - for studieforbund admin */}
            {user.role === 'admin' && (
              <NavLink to="/admin-settings" icon={<Settings size={18} />}>
                Innstillinger
              </NavLink>
            )}
            {/* Member Settings - for member role */}
            {user.role === 'member' && (
              <NavLink to="/member-settings" icon={<Settings size={18} />}>
                Innstillinger
              </NavLink>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-600">
            © {new Date().getFullYear()} Kompetanseforbundet. Alle rettigheter reservert.
          </p>
        </div>
      </footer>

      {/* Version Notifications Panel */}
      {user.role === 'member' && (
        <VersionNotificationsPanel
          isOpen={showVersionNotifications}
          onClose={() => setShowVersionNotifications(false)}
          onCountChange={setVersionNotificationsCount}
        />
      )}
    </div>
  );
}

interface NavLinkProps {
  to: string;
  icon: ReactNode;
  children: ReactNode;
  badge?: number;
}

function NavLink({ to, icon, children, badge }: NavLinkProps) {
  return (
    <Link
      to={to}
      className="flex items-center space-x-2 border-b-2 border-transparent hover:border-kf-primary text-gray-700 hover:text-kf-primary transition-colors px-1 relative"
    >
      {icon}
      <span className="relative">
        {children}
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-2 -right-5 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center z-10 shadow-md">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
    </Link>
  );
}
